/**
 * FootnoteExtension — Word-style footnotes and endnotes.
 *
 * Three nodes:
 *   - `footnoteRef`: inline atom rendered as an auto-numbered superscript marker
 *   - `footnotesSection`: block container near the end of the document (one per kind)
 *   - `footnoteItem`: a single note's content (block+), numbered to match its ref
 *
 * One ProseMirror plugin provides:
 *   - Auto-numbering: refs are numbered in document order (1, 2, 3… for
 *     footnotes; i, ii, iii… for endnotes). Numbers are rendered with
 *     decorations (`data-fn-num` + a clickable number widget on items), so the
 *     document JSON never stores stale numbers.
 *   - Integrity (appendTransaction): items whose ref was deleted are removed,
 *     refs without an item get an empty item, empty sections are removed, and
 *     items are kept in ref order.
 *   - Navigation: click a ref to jump to its note; click the note's number to
 *     jump back to the ref.
 *
 * v1 renders notes in a section at the document end (not at the bottom of each
 * page — see tiptap_editor_progress.md for the pagination rationale). DOCX
 * export still emits real Word footnotes, so Word displays them per page.
 */

import { Extension, Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as PMNode } from "@tiptap/pm/model";

export type FootnoteKind = "footnote" | "endnote";

export interface FootnoteInfo {
  footnoteId: string;
  kind: FootnoteKind;
  /** 1-based number in document order (per kind) */
  number: number;
  /** Display label: "3" for footnotes, "iii" for endnotes */
  label: string;
  /** Plain text of the note content (empty if no item exists yet) */
  text: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    footnote: {
      /** Insert a footnote/endnote ref at the cursor and an empty note to type into. */
      insertFootnote: (kind?: FootnoteKind) => ReturnType;
    };
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function generateFootnoteId(): string {
  return `fn-${Math.random().toString(36).substring(2, 10)}`;
}

const ROMAN: [number, string][] = [
  [1000, "m"],
  [900, "cm"],
  [500, "d"],
  [400, "cd"],
  [100, "c"],
  [90, "xc"],
  [50, "l"],
  [40, "xl"],
  [10, "x"],
  [9, "ix"],
  [5, "v"],
  [4, "iv"],
  [1, "i"],
];

export function toRoman(n: number): string {
  let result = "";
  let rest = n;
  for (const [value, sym] of ROMAN) {
    while (rest >= value) {
      result += sym;
      rest -= value;
    }
  }
  return result || "i";
}

function labelFor(kind: FootnoteKind, n: number): string {
  return kind === "endnote" ? toRoman(n) : String(n);
}

interface RefEntry {
  footnoteId: string;
  kind: FootnoteKind;
  pos: number;
  node: PMNode;
  /** true when the ref sits inside a footnoteItem (disallowed; removed by GC) */
  insideItem: boolean;
}

interface ItemEntry {
  footnoteId: string;
  kind: FootnoteKind;
  pos: number;
  node: PMNode;
}

interface SectionEntry {
  kind: FootnoteKind;
  pos: number;
  node: PMNode;
}

interface FootnoteScan {
  refs: RefEntry[];
  items: ItemEntry[];
  sections: SectionEntry[];
}

/** Walk the document and collect refs, items and sections in document order. */
export function scanFootnotes(doc: PMNode): FootnoteScan {
  const refs: RefEntry[] = [];
  const items: ItemEntry[] = [];
  const sections: SectionEntry[] = [];

  doc.descendants((node, pos, parent) => {
    if (node.type.name === "footnotesSection") {
      sections.push({
        kind: (node.attrs.kind as FootnoteKind) || "footnote",
        pos,
        node,
      });
      return true; // descend into items
    }
    if (node.type.name === "footnoteItem") {
      items.push({
        footnoteId: node.attrs.footnoteId,
        kind:
          parent?.type.name === "footnotesSection"
            ? (parent.attrs.kind as FootnoteKind) || "footnote"
            : "footnote",
        pos,
        node,
      });
      return true; // descend so refs nested inside items are found (and GC'd)
    }
    if (node.type.name === "footnoteRef") {
      let insideItem = false;
      // pos resolves inside the doc; check ancestor chain for footnoteItem
      const $pos = doc.resolve(pos);
      for (let d = $pos.depth; d > 0; d--) {
        if ($pos.node(d).type.name === "footnoteItem") {
          insideItem = true;
          break;
        }
      }
      refs.push({
        footnoteId: node.attrs.footnoteId,
        kind: (node.attrs.kind as FootnoteKind) || "footnote",
        pos,
        node,
        insideItem,
      });
      return false;
    }
    return true;
  });

  return { refs, items, sections };
}

/**
 * Compute the numbering map for the document: footnoteId → label.
 * Only refs outside items count; the first occurrence of an id wins.
 */
export function computeNumbering(
  doc: PMNode,
  precomputedScan?: FootnoteScan
): Map<string, { number: number; label: string; kind: FootnoteKind }> {
  // Accept an existing scan so hot paths (decorations on every keystroke)
  // walk the document once, not twice.
  const { refs } = precomputedScan ?? scanFootnotes(doc);
  const numbering = new Map<
    string,
    { number: number; label: string; kind: FootnoteKind }
  >();
  const counters: Record<FootnoteKind, number> = { footnote: 0, endnote: 0 };

  for (const ref of refs) {
    if (ref.insideItem) continue;
    if (numbering.has(ref.footnoteId)) continue;
    const n = ++counters[ref.kind];
    numbering.set(ref.footnoteId, {
      number: n,
      label: labelFor(ref.kind, n),
      kind: ref.kind,
    });
  }
  return numbering;
}

/**
 * List all footnotes/endnotes with their numbers and content text,
 * in reference order. Used by DOCX export and tests.
 */
export function getFootnotes(doc: PMNode): FootnoteInfo[] {
  const { items } = scanFootnotes(doc);
  const numbering = computeNumbering(doc);
  const itemsById = new Map(items.map(i => [i.footnoteId, i]));

  const result: FootnoteInfo[] = [];
  numbering.forEach((info, footnoteId) => {
    const item = itemsById.get(footnoteId);
    result.push({
      footnoteId,
      kind: info.kind,
      number: info.number,
      label: info.label,
      text: item ? item.node.textContent : "",
    });
  });
  return result;
}

// ---------------------------------------------------------------------------
// Nodes
// ---------------------------------------------------------------------------

export const FootnoteRef = Node.create({
  name: "footnoteRef",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,
  // Allow all marks so track-change insertion/deletion marks can wrap the ref
  marks: "_",

  addAttributes() {
    return {
      footnoteId: {
        default: null,
        parseHTML: el => el.getAttribute("data-footnote-id"),
        renderHTML: attrs =>
          attrs.footnoteId ? { "data-footnote-id": attrs.footnoteId } : {},
      },
      kind: {
        default: "footnote",
        parseHTML: el => el.getAttribute("data-kind") || "footnote",
        renderHTML: attrs => ({ "data-kind": attrs.kind || "footnote" }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "sup[data-footnote-id]" }];
  },

  renderHTML({ HTMLAttributes }) {
    // The number itself comes from the plugin decoration (data-fn-num)
    // rendered via CSS ::after, so stale numbers can never be persisted.
    return ["sup", mergeAttributes(HTMLAttributes, { class: "footnote-ref" })];
  },
});

export const FootnoteItem = Node.create({
  name: "footnoteItem",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      footnoteId: {
        default: null,
        parseHTML: el => el.getAttribute("data-footnote-id"),
        renderHTML: attrs =>
          attrs.footnoteId ? { "data-footnote-id": attrs.footnoteId } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-footnote-item]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-footnote-item": "true",
        class: "footnote-item",
      }),
      0,
    ];
  },
});

export const FootnotesSection = Node.create({
  name: "footnotesSection",
  group: "block",
  content: "footnoteItem+",
  defining: true,
  isolating: true,
  draggable: false,

  addAttributes() {
    return {
      kind: {
        default: "footnote",
        parseHTML: el => el.getAttribute("data-kind") || "footnote",
        renderHTML: attrs => ({ "data-kind": attrs.kind || "footnote" }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "section[data-footnotes-section]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "section",
      mergeAttributes(HTMLAttributes, {
        "data-footnotes-section": "true",
        class: "footnotes-section",
      }),
      0,
    ];
  },
});

// ---------------------------------------------------------------------------
// Plugin (numbering decorations + integrity + navigation)
// ---------------------------------------------------------------------------

const footnotePluginKey = new PluginKey("footnotePlugin");

function findSection(
  doc: PMNode,
  kind: FootnoteKind
): { pos: number; node: PMNode } | null {
  let found: { pos: number; node: PMNode } | null = null;
  doc.forEach((child, offset) => {
    if (
      !found &&
      child.type.name === "footnotesSection" &&
      (child.attrs.kind || "footnote") === kind
    ) {
      found = { pos: offset, node: child };
    }
  });
  return found;
}

function buildDecorations(doc: PMNode): DecorationSet {
  const scan = scanFootnotes(doc);
  const { refs, items } = scan;
  const numbering = computeNumbering(doc, scan);
  const decorations: Decoration[] = [];

  for (const ref of refs) {
    if (ref.insideItem) continue;
    const info = numbering.get(ref.footnoteId);
    decorations.push(
      Decoration.node(ref.pos, ref.pos + ref.node.nodeSize, {
        "data-fn-num": info ? info.label : "?",
      })
    );
  }

  for (const item of items) {
    const info = numbering.get(item.footnoteId);
    const label = info ? info.label : "?";
    const footnoteId = item.footnoteId;

    // Place the clickable number inside the first child paragraph so it
    // renders inline with the note text (falls back to the item start when
    // the first child is not a paragraph).
    const firstChild = item.node.firstChild;
    const widgetPos =
      firstChild && firstChild.type.name === "paragraph"
        ? item.pos + 2
        : item.pos + 1;

    decorations.push(
      Decoration.widget(
        widgetPos,
        () => {
          const span = document.createElement("span");
          span.className = "footnote-num-backref";
          span.setAttribute("data-target-footnote-id", footnoteId || "");
          span.setAttribute("contenteditable", "false");
          span.title = "Go to reference";
          span.textContent = `${label}.`;
          return span;
        },
        { side: -1, key: `fn-num-${footnoteId}-${label}` }
      )
    );
  }

  return DecorationSet.create(doc, decorations);
}

export const FootnotePlugin = Extension.create({
  name: "footnotePlugin",

  addStorage() {
    return {
      // Hosts can set this true while doing bulk programmatic replaces
      // if integrity maintenance should pause (not normally needed).
      suppressIntegrity: false,
    };
  },

  addCommands() {
    return {
      insertFootnote:
        (kind: FootnoteKind = "footnote") =>
        ({ state, dispatch }) => {
          const { schema, selection } = state;
          const refType = schema.nodes.footnoteRef;
          const itemType = schema.nodes.footnoteItem;
          const sectionType = schema.nodes.footnotesSection;
          const paragraphType = schema.nodes.paragraph;
          if (!refType || !itemType || !sectionType || !paragraphType)
            return false;

          // Disallow inserting a footnote inside a footnote item
          const $from = selection.$from;
          for (let d = $from.depth; d > 0; d--) {
            if ($from.node(d).type.name === "footnoteItem") return false;
          }

          if (!dispatch) return true;

          const footnoteId = generateFootnoteId();
          const tr = state.tr;

          // 1. Insert the ref at the cursor
          tr.replaceSelectionWith(refType.create({ footnoteId, kind }), false);

          // 2. Append an empty item to the section (create section if needed)
          const paragraph = paragraphType.createAndFill();
          if (!paragraph) return false;
          const item = itemType.create({ footnoteId }, paragraph);

          const existing = findSection(tr.doc, kind);
          let caretPos: number;
          if (existing) {
            const insertAt = existing.pos + existing.node.nodeSize - 1;
            tr.insert(insertAt, item);
            caretPos = insertAt + 2; // +1 into item, +1 into paragraph
          } else {
            let insertAt = tr.doc.content.size;
            if (kind === "footnote") {
              // Footnotes section always precedes the endnotes section
              const endnotes = findSection(tr.doc, "endnote");
              if (endnotes) insertAt = endnotes.pos;
            }
            const section = sectionType.create({ kind }, item);
            tr.insert(insertAt, section);
            caretPos = insertAt + 3; // +1 into section, +1 into item, +1 into paragraph
          }

          // 3. Move the caret into the new note so the user can type immediately
          tr.setSelection(
            TextSelection.create(tr.doc, caretPos)
          ).scrollIntoView();
          tr.setMeta("footnoteInsert", true);
          dispatch(tr);
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Alt-f": () => this.editor.commands.insertFootnote("footnote"),
    };
  },

  addProseMirrorPlugins() {
    const extensionStorage = this.storage;

    return [
      new Plugin({
        key: footnotePluginKey,

        state: {
          init: (_, state) => buildDecorations(state.doc),
          apply: (tr, old) =>
            tr.docChanged
              ? buildDecorations(tr.doc)
              : old.map(tr.mapping, tr.doc),
        },

        props: {
          decorations(state) {
            return this.getState(state);
          },

          // All jump navigation runs on MOUSEDOWN at the DOM level:
          //  - handleClickOn can't work for refs — the <sup> is an EMPTY
          //    atom whose number is a CSS ::after pseudo-element, so
          //    posAtCoords resolves clicks to a position NEXT TO the atom
          //    and the footnoteRef node is never the clicked node.
          //  - plugin "click" handlers never fire in this setup either
          //    (verified live); "mousedown" does.
          handleDOMEvents: {
            mousedown(view, event) {
              const targetEl = event.target as HTMLElement | null;

              // Note's number → jump back to its ref
              const backref = targetEl?.closest?.(
                ".footnote-num-backref"
              ) as HTMLElement | null;
              if (backref) {
                const footnoteId = backref.getAttribute(
                  "data-target-footnote-id"
                );
                if (!footnoteId) return false;
                const { refs } = scanFootnotes(view.state.doc);
                const ref = refs.find(
                  r => r.footnoteId === footnoteId && !r.insideItem
                );
                if (!ref) return false;
                event.preventDefault();
                const tr = view.state.tr
                  .setSelection(
                    TextSelection.create(
                      view.state.doc,
                      ref.pos + ref.node.nodeSize
                    )
                  )
                  .scrollIntoView();
                view.dispatch(tr);
                view.focus();
                return true;
              }

              // Ref → jump to its note. The dispatch is DEFERRED past the
              // mouseup/click cycle: the ref is a selectable atom and
              // ProseMirror's own click handling re-selects the node after
              // mousedown, overriding an immediate jump.
              const refEl = targetEl?.closest?.(
                "sup.footnote-ref[data-footnote-id]"
              ) as HTMLElement | null;
              if (refEl) {
                const footnoteId = refEl.getAttribute("data-footnote-id");
                if (!footnoteId) return false;
                event.preventDefault();
                setTimeout(() => {
                  const { items } = scanFootnotes(view.state.doc);
                  const item = items.find(i => i.footnoteId === footnoteId);
                  if (!item) return;
                  const tr = view.state.tr
                    .setSelection(
                      TextSelection.create(view.state.doc, item.pos + 2)
                    )
                    .scrollIntoView();
                  view.dispatch(tr);
                  view.focus();
                }, 0);
                return true;
              }

              return false;
            },
          },
        },

        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some(tr => tr.docChanged)) return null;
          if (extensionStorage.suppressIntegrity) return null;

          const { refs, items, sections } = scanFootnotes(newState.doc);

          const validRefs = refs.filter(r => !r.insideItem);
          const refIds = new Set(validRefs.map(r => r.footnoteId));
          const itemIds = new Set(items.map(i => i.footnoteId));

          const tr = newState.tr;
          let modified = false;

          // 1. Delete refs that ended up inside items (e.g. via paste) and
          //    orphaned items whose ref no longer exists. A section requires
          //    at least one item (content: "footnoteItem+"), so when ALL of a
          //    section's items are orphaned the whole section is deleted —
          //    deleting just the items would make ProseMirror auto-fill a
          //    blank item to satisfy the schema. Delete bottom-up so earlier
          //    positions stay valid.
          const deletions: { from: number; to: number }[] = [];
          for (const ref of refs) {
            if (ref.insideItem) {
              deletions.push({
                from: ref.pos,
                to: ref.pos + ref.node.nodeSize,
              });
            }
          }

          const isOrphan = (node: PMNode) =>
            !node.attrs.footnoteId || !refIds.has(node.attrs.footnoteId);

          const sectionRanges = sections.map(s => ({
            from: s.pos,
            to: s.pos + s.node.nodeSize,
          }));
          for (const section of sections) {
            const orphanRanges: { from: number; to: number }[] = [];
            let liveCount = 0;
            section.node.forEach((child, offset) => {
              if (isOrphan(child)) {
                const from = section.pos + 1 + offset;
                orphanRanges.push({ from, to: from + child.nodeSize });
              } else {
                liveCount++;
              }
            });
            if (liveCount === 0 && orphanRanges.length > 0) {
              deletions.push({
                from: section.pos,
                to: section.pos + section.node.nodeSize,
              });
            } else {
              deletions.push(...orphanRanges);
            }
          }
          // Bare items outside any section (legal per schema, e.g. pasted)
          for (const item of items) {
            const inSection = sectionRanges.some(
              r => item.pos >= r.from && item.pos < r.to
            );
            if (!inSection && isOrphan(item.node)) {
              deletions.push({
                from: item.pos,
                to: item.pos + item.node.nodeSize,
              });
            }
          }

          // Drop ranges fully contained in another (an inside-item ref's
          // range nests inside its orphaned section's range) — bottom-up
          // deletion is only position-safe for DISJOINT ranges; deleting a
          // nested range first shifts the outer range past doc end.
          const kept: { from: number; to: number }[] = [];
          for (const d of deletions) {
            if (kept.some(k => k.from <= d.from && k.to >= d.to)) continue;
            for (let i = kept.length - 1; i >= 0; i--) {
              if (d.from <= kept[i].from && d.to >= kept[i].to) {
                kept.splice(i, 1);
              }
            }
            kept.push(d);
          }

          kept.sort((a, b) => b.from - a.from);
          for (const del of kept) {
            tr.delete(del.from, del.to);
            modified = true;
          }

          // 2. Create missing items for refs that have none (e.g. after a
          //    partial undo). Append to the matching section, creating it on
          //    demand. Operate on tr.doc so positions reflect step 1.
          const missing = validRefs.filter(
            (r, idx) =>
              !itemIds.has(r.footnoteId) &&
              validRefs.findIndex(o => o.footnoteId === r.footnoteId) === idx
          );
          for (const ref of missing) {
            const schema = newState.schema;
            const paragraph = schema.nodes.paragraph.createAndFill();
            if (!paragraph) continue;
            const item = schema.nodes.footnoteItem.create(
              { footnoteId: ref.footnoteId },
              paragraph
            );
            const section = findSection(tr.doc, ref.kind);
            if (section) {
              tr.insert(section.pos + section.node.nodeSize - 1, item);
            } else {
              let insertAt = tr.doc.content.size;
              if (ref.kind === "footnote") {
                const endnotes = findSection(tr.doc, "endnote");
                if (endnotes) insertAt = endnotes.pos;
              }
              tr.insert(
                insertAt,
                schema.nodes.footnotesSection.create({ kind: ref.kind }, item)
              );
            }
            modified = true;
          }

          // 3. Safety net: remove sections whose every item is orphaned
          //    (covers schema auto-fill remnants from other code paths).
          //    (Re-scan tr.doc since steps 1–2 may have changed it.)
          if (modified || sections.length > 0) {
            const current = scanFootnotes(tr.doc);
            const currentRefIds = new Set(
              current.refs.filter(r => !r.insideItem).map(r => r.footnoteId)
            );
            const sectionDeletions = current.sections
              .filter(s => {
                let live = 0;
                s.node.forEach(child => {
                  if (
                    child.attrs.footnoteId &&
                    currentRefIds.has(child.attrs.footnoteId)
                  ) {
                    live++;
                  }
                });
                return live === 0;
              })
              .sort((a, b) => b.pos - a.pos);
            for (const s of sectionDeletions) {
              tr.delete(s.pos, s.pos + s.node.nodeSize);
              modified = true;
            }

            // 4. Keep items in ref order within each section.
            // (Reuse the step-3 scan unless step 3 changed the doc.)
            const after =
              sectionDeletions.length > 0 ? scanFootnotes(tr.doc) : current;
            const orderedIds = (kind: FootnoteKind) => {
              const seen = new Set<string>();
              const ordered: string[] = [];
              for (const r of after.refs) {
                if (r.insideItem || r.kind !== kind) continue;
                if (!seen.has(r.footnoteId)) {
                  seen.add(r.footnoteId);
                  ordered.push(r.footnoteId);
                }
              }
              return ordered;
            };

            for (const section of after.sections) {
              const want = orderedIds(section.kind);
              const have: string[] = [];
              section.node.forEach(child => {
                have.push(child.attrs.footnoteId);
              });
              const wantSet = new Set(want);
              const haveSet = new Set(have);
              const haveFiltered = have.filter(id => wantSet.has(id));
              const wantFiltered = want.filter(id => haveSet.has(id));
              const inOrder =
                haveFiltered.length === wantFiltered.length &&
                haveFiltered.every((id, i) => id === wantFiltered[i]);
              if (!inOrder) {
                const byId = new Map<string, PMNode>();
                section.node.forEach(child => {
                  byId.set(child.attrs.footnoteId, child);
                });
                const sorted: PMNode[] = [];
                for (const id of wantFiltered) {
                  const n = byId.get(id);
                  if (n) sorted.push(n);
                }
                // Keep any items not in the ref order at the end (shouldn't
                // happen after GC, but never drop content)
                for (const id of have) {
                  if (!wantFiltered.includes(id)) {
                    const n = byId.get(id);
                    if (n) sorted.push(n);
                  }
                }
                if (sorted.length > 0) {
                  tr.replaceWith(
                    section.pos,
                    section.pos + section.node.nodeSize,
                    newState.schema.nodes.footnotesSection.create(
                      section.node.attrs,
                      sorted
                    )
                  );
                  modified = true;
                }
              }
            }
          }

          if (!modified) return null;
          tr.setMeta("footnoteIntegrity", true);
          tr.setMeta("addToHistory", true);
          return tr;
        },
      }),
    ];
  },
});

/** Convenience bundle: register all four pieces at once. */
export const FootnoteExtensions = [
  FootnoteRef,
  FootnoteItem,
  FootnotesSection,
  FootnotePlugin,
];
